import React from 'react';
import styled from 'styled-components';

import { UniverseReference, User, WellnessBasketPrices } from '@front/common/src/types/commons';
import { CommonModalProps } from '@front/common/src/types/components';
import { useConfig } from '@front/common/src/providers/config-provider';
import { getBasketPrices, getPrestationCount } from '@front/common/src/ducks/customer/basket-selectors';

import {
  getMembershipPaywallCTAText,
  getMembershipQuestions,
  MembershipOptIn,
} from '@front/customer/src/modals/membership-paywall/utils';
import useMembershipOptIn from '@front/customer/src/hooks/membership/use-membership-opt-in';

import { translate } from '@front/i18n';

import FullHeightCenteredModal from '@front/web-ui/src/components/modal/full-height-centered-modal';
import Button from '@front/web-ui/src/components/button/button';

import {
  StyledStickyBottomWrapper,
  MembershipFaq,
  StyledWhiteSpacerRoundedBorders,
  Content,
  PriceCardsContainer,
  StickyContainer,
} from '../common';
import WellnessCleaningSpotHeader from '../wellness-cleaning-spot-header';
import { useAppSelector } from '../../../store';

import SelectableWellnessStandardPriceCard from './selectable-wellness-standard-price-card';
import SelectableWellnessMemberPriceCard from './selectable-wellness-member-price-card';

const t = translate('modals.membership_paywall');

const StyledSpacer = styled.div`
  height: 20px;
`;

type WellnessMembershipPaywallProps = {
  isOpen: CommonModalProps['isOpen'];
  universeReference: UniverseReference;
  optInChoice?: MembershipOptIn;
  closePaywall?: () => void;
  onConfirmPaywall: () => void;
  onOptInChoiceSelection: (newOptIn: MembershipOptIn) => void;
  user?: User;
};

const WellnessMembershipPaywall = ({
  isOpen,
  universeReference,
  optInChoice,
  closePaywall,
  onConfirmPaywall,
  onOptInChoiceSelection,
  user,
}: WellnessMembershipPaywallProps) => {
  const config = useConfig();
  const prices = useAppSelector((state) => getBasketPrices(state, config)) as WellnessBasketPrices;
  const numberOfSelectedPrestations = useAppSelector(getPrestationCount);
  const { isLegallyCompliantMembershipOptIn } = useMembershipOptIn({ user });

  return (
    <FullHeightCenteredModal isOpen={isOpen} onClose={closePaywall}>
      <StickyContainer>
        <Content>
          <WellnessCleaningSpotHeader />
          <StyledWhiteSpacerRoundedBorders />
          <PriceCardsContainer>
            {isLegallyCompliantMembershipOptIn ? (
              <>
                <SelectableWellnessMemberPriceCard
                  optInChoice={optInChoice}
                  onPress={onOptInChoiceSelection}
                  prices={prices}
                  numberOfSelectedPrestations={numberOfSelectedPrestations}
                  hasFullWidthLabel
                />
                <StyledSpacer />
                <SelectableWellnessStandardPriceCard
                  optInChoice={optInChoice}
                  onPress={onOptInChoiceSelection}
                  prices={prices}
                  numberOfSelectedPrestations={numberOfSelectedPrestations}
                />
              </>
            ) : (
              <>
                <SelectableWellnessStandardPriceCard
                  optInChoice={optInChoice}
                  onPress={onOptInChoiceSelection}
                  prices={prices}
                  numberOfSelectedPrestations={numberOfSelectedPrestations}
                />
                <StyledSpacer />
                <SelectableWellnessMemberPriceCard
                  optInChoice={optInChoice}
                  onPress={onOptInChoiceSelection}
                  prices={prices}
                  numberOfSelectedPrestations={numberOfSelectedPrestations}
                />
              </>
            )}
          </PriceCardsContainer>
          <MembershipFaq
            title={t('.faq.title')}
            questions={getMembershipQuestions({
              universeReference,
            })}
          />
        </Content>
        <StyledStickyBottomWrapper>
          <Button size='lg' onPress={onConfirmPaywall} hasFullWidth isDisabled={!optInChoice}>
            {getMembershipPaywallCTAText(optInChoice)}
          </Button>
        </StyledStickyBottomWrapper>
      </StickyContainer>
    </FullHeightCenteredModal>
  );
};

export default WellnessMembershipPaywall;
